import { useCallback, useRef, useState } from 'react';
import { fetchTransitionAdvice } from '../../api/endpoints';
import { ApiError, toApiError } from '../../api/errors';
import { isAbort } from '../../api/http';
import type { TicketListItem } from '../../api/types';
import { adoptRefusal, decideDrop, type DropTarget, type TransitionState } from './drag-rules';
import type { BoardColumn } from './lane-columns';
import { transitionCard, type GitlabHookOutcome, type TransitionRefusal } from './transition-api';

/**
 * Drag → transition.
 *
 * 🔴 THE MOMENT A CARD IS PICKED UP this asks `GET /tickets/:id/transitions` — and since PH-19 the
 * answer it wants is BOTH halves: the SUGGESTED next statuses (which order what is offered) and
 * `transitions_are_advisory` (which says whether the suggestion is advice or a rule on this server).
 * ⛔ Until that answer arrives no column accepts a drop, because without it the board does not know
 * WHICH semantics it is under — not because a missing suggestion forbids anything.
 *
 * ⭐ One request per DRAG, not per card. Fetching transitions for every card on load would be one
 * request per ticket on a 500-ticket board — the client-side N+1 the API's own list query goes out of
 * its way to avoid.
 */

export interface PendingChoice {
  ticket: TicketListItem;
  column: BoardColumn;
  targets: DropTarget[];
}

export interface DragError {
  ticket: TicketListItem;
  /** The API's own 409, kept whole. */
  refusal: TransitionRefusal | null;
  /** Anything that was not a 409 — rendered by the shared ErrorNotice. */
  error: ApiError | null;
}

export function useCardDrag(
  onChanged: () => void,
  /**
   * 🔴 THE ACTIVE STATUS CODES from `VocabularyProvider` — the only thing that still narrows a drop
   * under advisory semantics. ⛔ Passed in rather than read here so this hook has no opinion about
   * where the vocabulary comes from, and so a test can hand it a set with a retired status in it.
   */
  activeStatuses: ReadonlySet<string>,
) {
  const [ticket, setTicket] = useState<TicketListItem | null>(null);
  const [transitions, setTransitions] = useState<TransitionState>({ kind: 'idle' });
  const [choice, setChoice] = useState<PendingChoice | null>(null);
  const [failure, setFailure] = useState<DragError | null>(null);
  /**
   * PH-29 — the GitLab hook's own words about the LAST successful move, or null.
   * ⛔ Deliberately NOT part of `DragError`: this is a notice about a move that WORKED, and putting it
   *   in the error slot is how "your move succeeded" becomes "your move failed" on screen.
   */
  const [gitlabNotice, setGitlabNotice] = useState<GitlabHookOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * ⭐⭐ THE TARGETS THE SERVER HAS REFUSED DURING THIS DRAG, and the replacement for the old
   * "adopt the refusal as the new permission list". A 409 withdraws THE STATUS IT NAMED and nothing
   * else; narrowing the whole board to whatever the refusal happened to list would re-impose the
   * gate PH-19 removed, from a message that was about one status.
   */
  const [refusedStatuses, setRefusedStatuses] = useState<ReadonlySet<string>>(new Set());
  const requestRef = useRef<AbortController | null>(null);

  const begin = useCallback((card: TicketListItem) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    setTicket(card);
    setFailure(null);
    setRefusedStatuses(new Set());
    setTransitions({ kind: 'loading' });

    fetchTransitionAdvice(card.id, controller.signal)
      .then(({ suggested, advisory }) => setTransitions({ kind: 'ready', suggested, advisory }))
      .catch((err: unknown) => {
        if (isAbort(err)) return;
        const apiError = toApiError(err, `/tickets/${card.id}/transitions`);
        // ⛔ NOT an empty list. "We could not ask" and "nothing is suggested" are different facts, and
        // an empty list here would look like an answer the server never gave.
        setTransitions({ kind: 'error', message: apiError.reason ?? apiError.message });
      });
  }, []);

  const end = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    setTicket(null);
    setTransitions({ kind: 'idle' });
  }, []);

  const perform = useCallback(
    async (card: TicketListItem, toStatus: string) => {
      setBusy(true);
      setFailure(null);
      try {
        const outcome = await transitionCard(card.id, toStatus);
        if (outcome.ok) {
          setChoice(null);
          /**
           * PH-29 — WHAT THE GITLAB HOOK DID IS NOW SAID OUT LOUD.
           *
           * ⛔ THE MOVE SUCCEEDED EITHER WAY, so this is a NOTICE and never a failure: the transition
           *   is a PlanHub fact already committed, and `gitlab-link.ts` cannot throw precisely so that
           *   GitLab being unreachable does not undo it. Rendering this as an error would tell the
           *   reader their move did not happen, which is false.
           *
           * ⚠️ `undefined` means the transition had nothing to do with GitLab and produces NOTHING.
           *   Only a present, explicitly-false `ok` is a problem worth a line on screen.
           */
          setGitlabNotice(outcome.gitlab ?? null);
          // ⛔ The card is NOT moved optimistically. A refused move would leave the board showing a
          // status the ticket does not have — "stale looks current" wearing a different hat.
          onChanged();
        } else {
          // ⭐ A refusal means the hook never ran, so any notice from a PREVIOUS move must go. Leaving
          //   it would attach "GitLab issue #1 created" to a move that was just rejected.
          setGitlabNotice(null);
          setFailure({ ticket: card, refusal: outcome.refusal, error: null });
          // ⭐ The refusal's SUGGESTIONS are fresher than ours, so they are adopted — as suggestions.
          // ⛔ They do not become a permission list; `adoptRefusal` documents why.
          setTransitions((current) => adoptRefusal(current, outcome.refusal));
          // ⭐⭐ THE TARGET IT REFUSED IS WITHDRAWN, and only that one. The reader's first instinct
          // after a failure is to try the same drop again, and here that is guaranteed to fail
          // identically — so the board stops offering it for the rest of this drag.
          setRefusedStatuses((current) => new Set([...current, toStatus]));
        }
      } catch (err) {
        setFailure({ ticket: card, refusal: null, error: toApiError(err, '/tickets/transition') });
      } finally {
        setBusy(false);
      }
    },
    [onChanged],
  );

  const drop = useCallback(
    (column: BoardColumn) => {
      const card = ticket;
      if (!card) return;
      const decision = decideDrop({
        column,
        ticketStatus: card.status,
        transitions,
        activeStatuses,
        refusedStatuses,
      });

      if (decision.kind === 'ok') {
        void perform(card, decision.toStatus);
      } else if (decision.kind === 'choose') {
        // ⭐ A lane is not a status. With several possible targets inside it there is no single move
        // to perform, and picking one would be the UI choosing a workflow step on the user's behalf.
        setChoice({ ticket: card, column, targets: decision.targets });
      }
      // A refusal cannot arrive here: `droppable` is false, so the drop never fires.
      end();
    },
    [ticket, transitions, activeStatuses, refusedStatuses, perform, end],
  );

  const decide = useCallback(
    (column: BoardColumn) => {
      if (!ticket) return null;
      return decideDrop({
        column,
        ticketStatus: ticket.status,
        transitions,
        activeStatuses,
        refusedStatuses,
      });
    },
    [ticket, transitions, activeStatuses, refusedStatuses],
  );

  return {
    ticket,
    transitions,
    choice,
    failure,
    busy,
    /** Targets a 409 has refused during this drag — withdrawn until the card is picked up again. */
    refusedStatuses,
    activeStatuses,
    begin,
    end,
    drop,
    decide,
    chooseTarget: perform,
    cancelChoice: useCallback(() => setChoice(null), []),
    dismissFailure: useCallback(() => setFailure(null), []),
    /**
     * PH-29 — the GitLab hook's result for the last SUCCESSFUL move, or null.
     * ⛔ Never rendered as an error: the transition committed regardless. See `perform`.
     */
    gitlabNotice,
    dismissGitlabNotice: useCallback(() => setGitlabNotice(null), []),
  };
}
